import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, type EmailDelivery, type EmailDeliveryRange } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import CenteredSpinner from "../../CenteredSpinner.tsx";
import Panel from "../../Panel.tsx";
import { formatNumber, timeAgo, utcDayLabel } from "../../../utils/format.ts";

const SENT = "var(--mui-palette-primary-main)";
const FAILED = "var(--mui-palette-error-main)";
const RETRIED = "var(--mui-palette-warning-main)";
const SURFACE = "var(--mui-palette-background-paper)";
const MUTED = "var(--mui-palette-text-secondary)";
const GRID = "var(--mui-palette-divider)";

interface Point {
  t: number;
  sent: number;
  failed: number;
  retried: number;
}

const hourLabel = (t: number) => new Date(t).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

function bucketLabel(t: number, bucket: EmailDelivery["bucket"]) {
  if (bucket === "day") return utcDayLabel(t);
  return `${new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${hourLabel(t)}`;
}

function Swatch({ color, line }: { color: string; line?: boolean }) {
  return line ? (
    <Box sx={{ width: 12, height: 0, borderTop: `2px dashed ${color}` }} />
  ) : (
    <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color }} />
  );
}

function LegendRow({ color, line, label, value }: { color: string; line?: boolean; label: string; value?: number }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Swatch color={color} line={line} />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      {value !== undefined && (
        <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(value)}
        </Typography>
      )}
    </Stack>
  );
}

function ChartTooltip({
  active,
  payload,
  bucket,
}: {
  active?: boolean;
  payload?: { payload: Point }[];
  bucket: EmailDelivery["bucket"];
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Paper elevation={4} sx={{ px: 1.5, py: 1, borderRadius: 2, minWidth: 170 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {bucketLabel(p.t, bucket)}
      </Typography>
      <LegendRow color={SENT} label="Sent" value={p.sent} />
      <LegendRow color={FAILED} label="Failed" value={p.failed} />
      <LegendRow color={RETRIED} line label="Retries" value={p.retried} />
    </Paper>
  );
}

function Figure({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.3 }}>
        {formatNumber(value)}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Outbound email through Scaleway: emails that finished sent or failed per hour
 * (48 h) or day (14 days), the retry submissions behind them, what is still in
 * flight, and the latest failures with Scaleway's reason.
 */
export default function EmailDeliveryPanel() {
  const [range, setRange] = useState<EmailDeliveryRange>("48h");
  const delivery = useQuery({
    queryKey: keys.platform.emails(range),
    queryFn: () => api.getEmailDelivery(range),
    meta: { live: true },
  });
  const data = delivery.data;
  const points: Point[] = (data?.buckets ?? []).map((b) => ({
    t: Date.parse(b.at),
    sent: b.sent,
    failed: b.failed,
    retried: b.retried,
  }));
  const inFlight = data ? data.open.queued + data.open.accepted : 0;

  return (
    <Panel
      title="Email delivery"
      subtitle="Emails sent through Scaleway Transactional Email, and the retries it took"
      action={
        <ToggleButtonGroup
          exclusive
          size="small"
          value={range}
          onChange={(_, next: EmailDeliveryRange | null) => next && setRange(next)}
          aria-label="Time range"
        >
          <ToggleButton value="48h">48 hours</ToggleButton>
          <ToggleButton value="14d">14 days</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {!data ? (
        <CenteredSpinner />
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={4} useFlexGap sx={{ flexWrap: "wrap" }}>
            <Figure label="Sent" value={data.totals.sent} />
            <Figure label="Failed" value={data.totals.failed} />
            <Figure label="Retries" value={data.totals.retried} />
            <Figure
              label="In flight"
              value={inFlight}
              hint={
                data.open.waiting_retry
                  ? `${formatNumber(data.open.waiting_retry)} waiting to retry`
                  : data.open.accepted
                    ? `${formatNumber(data.open.accepted)} awaiting delivery`
                    : undefined
              }
            />
          </Stack>
          <Box>
            <Stack direction="row" spacing={2.5} sx={{ mb: 1.5 }}>
              <LegendRow color={SENT} label="Sent" />
              <LegendRow color={FAILED} label="Failed" />
              <LegendRow color={RETRIED} line label="Retries" />
            </Stack>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={points} margin={{ top: 4, right: 0, bottom: 0, left: -12 }} barCategoryGap="20%">
                <CartesianGrid vertical={false} stroke={GRID} strokeOpacity={0.6} />
                <XAxis
                  dataKey="t"
                  tickFormatter={(t: number) => (data.bucket === "day" ? utcDayLabel(t) : hourLabel(t))}
                  tick={{ fill: MUTED, fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: GRID }}
                  minTickGap={24}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: MUTED, fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip
                  cursor={{ fill: "var(--mui-palette-action-hover)" }}
                  content={<ChartTooltip bucket={data.bucket} />}
                  isAnimationActive={false}
                />
                <Bar dataKey="sent" stackId="e" fill={SENT} stroke={SURFACE} strokeWidth={1} isAnimationActive={false} />
                <Bar
                  dataKey="failed"
                  stackId="e"
                  fill={FAILED}
                  stroke={SURFACE}
                  strokeWidth={1}
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                />
                <Line
                  dataKey="retried"
                  type="monotone"
                  stroke={RETRIED}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
          {data.recent_failures.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Latest failures
              </Typography>
              <Stack spacing={0.5}>
                {data.recent_failures.map((f) => (
                  <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: "baseline", minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0, minWidth: 72 }}>
                      {f.finished_at ? timeAgo(f.finished_at) : "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                      {f.template}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                      {f.attempts === 1 ? "1 attempt" : `${f.attempts} attempts`}
                    </Typography>
                    <Typography variant="body2" noWrap title={f.error ?? undefined} sx={{ minWidth: 0 }}>
                      {f.error ?? "No reason given"}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Panel>
  );
}
